/**
 * Flowcore Video Player
 * Lightweight, universal HLS player with zero framework dependencies
 */

import Hls from 'hls.js';

export interface PlayerOptions {
  // Container
  container: string | HTMLElement;

  // Video source (pick one)
  videoId?: string;
  streamUrl?: string;

  // API config (if using videoId)
  apiUrl?: string;
  apiKey?: string;
  accessToken?: string;

  // Playback
  autoplay?: boolean;
  muted?: boolean;
  loop?: boolean;
  controls?: boolean;

  // Appearance
  poster?: string;
  className?: string;

  // Subtitles
  subtitles?: Array<{
    src: string;
    srclang: string;
    label: string;
    default?: boolean;
  }>;

  // Callbacks
  onReady?: () => void;
  onPlay?: () => void;
  onPause?: () => void;
  onEnded?: () => void;
  onError?: (error: Error) => void;
  onTimeUpdate?: (currentTime: number) => void;
  onProgress?: (percent: number) => void;
}

export class FlowcorePlayer {
  private container: HTMLElement;
  private video: HTMLVideoElement;
  private hls: Hls | null = null;
  private options: PlayerOptions;
  private streamUrl: string | null = null;

  // Custom controls
  private controlsContainer: HTMLElement | null = null;
  private controlsTimeout: number | null = null;
  private isSeeking = false;

  constructor(options: PlayerOptions) {
    this.options = options;

    // Get container
    if (typeof options.container === 'string') {
      const el = document.querySelector(options.container);
      if (!el) throw new Error(`Container not found: ${options.container}`);
      this.container = el as HTMLElement;
    } else {
      this.container = options.container;
    }

    // Create video element
    this.video = document.createElement('video');
    this.video.className = 'flowcore-player__video';

    if (options.poster) this.video.poster = options.poster;
    if (options.autoplay) this.video.autoplay = true;
    if (options.muted) this.video.muted = true;
    if (options.loop) this.video.loop = true;

    // Never use native controls - we'll build our own
    this.video.controls = false;

    if (options.className) this.container.className += ` ${options.className}`;

    // Add player class
    this.container.classList.add('flowcore-player');
    this.container.appendChild(this.video);

    // Add subtitle tracks if provided
    if (this.options.subtitles && this.options.subtitles.length > 0) {
      this.options.subtitles.forEach((subtitle, _index) => {
        const track = document.createElement('track');
        track.kind = 'subtitles';
        track.src = subtitle.src;
        track.srclang = subtitle.srclang;
        track.label = subtitle.label;
        if (subtitle.default) track.default = true;

        // Add event listeners for debugging
        track.addEventListener('load', () => {
          console.log(`✅ Subtitle track loaded: ${subtitle.label} (${subtitle.srclang})`);
        });
        track.addEventListener('error', (e) => {
          console.error(`❌ Subtitle track error for ${subtitle.label}:`, e);
          console.error(`   URL: ${subtitle.src}`);
        });

        this.video.appendChild(track);
      });
    }

    // Create custom controls if enabled
    if (options.controls !== false) {
      this.createCustomControls();
    }

    // Initialize
    this.init();
  }

  private createCustomControls() {
    // Main controls container
    this.controlsContainer = document.createElement('div');
    this.controlsContainer.className = 'flowcore-player__controls';

    // Controls HTML structure (Netflix-style)
    this.controlsContainer.innerHTML = `
      <div class="flowcore-player__controls-gradient"></div>
      <div class="flowcore-player__controls-bottom">
        <!-- Progress bar -->
        <div class="flowcore-player__progress-container">
          <div class="flowcore-player__progress-bar">
            <div class="flowcore-player__progress-buffered"></div>
            <div class="flowcore-player__progress-played"></div>
            <div class="flowcore-player__progress-handle"></div>
          </div>
        </div>
        
        <!-- Control buttons -->
        <div class="flowcore-player__controls-row">
          <div class="flowcore-player__controls-left">
            <!-- Play/Pause -->
            <button class="flowcore-player__btn flowcore-player__btn-play" aria-label="Play">
              <svg class="flowcore-player__icon flowcore-player__icon-play" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z"/>
              </svg>
              <svg class="flowcore-player__icon flowcore-player__icon-pause" viewBox="0 0 24 24" style="display:none;">
                <path d="M6 4h4v16H6zM14 4h4v16h-4z"/>
              </svg>
            </button>
            
            <!-- Volume -->
            <div class="flowcore-player__volume-container">
              <button class="flowcore-player__btn flowcore-player__btn-volume" aria-label="Mute">
                <svg class="flowcore-player__icon flowcore-player__icon-volume-high" viewBox="0 0 24 24">
                  <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                </svg>
                <svg class="flowcore-player__icon flowcore-player__icon-volume-muted" viewBox="0 0 24 24" style="display:none;">
                  <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                </svg>
              </button>
              <div class="flowcore-player__volume-slider-container">
                <input type="range" class="flowcore-player__volume-slider" min="0" max="100" value="100" aria-label="Volume">
              </div>
            </div>
            
            <!-- Time -->
            <div class="flowcore-player__time">
              <span class="flowcore-player__time-current">0:00</span>
              <span class="flowcore-player__time-separator">/</span>
              <span class="flowcore-player__time-duration">0:00</span>
            </div>
          </div>
          
          <div class="flowcore-player__controls-right">
            <!-- Subtitle selector -->
            ${
              this.options.subtitles && this.options.subtitles.length > 0
                ? `
            <div class="flowcore-player__subtitle-container">
              <button class="flowcore-player__btn flowcore-player__btn-subtitle" aria-label="Subtitles">
                <svg class="flowcore-player__icon" viewBox="0 0 24 24">
                  <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zM4 12h4v2H4v-2zm6 0h4v2h-4v-2zm-6 4h4v2H4v-2zm6 0h4v2h-4v-2zm10-6h-4v-2h4v2zm0 4h-4v-2h4v2z"/>
                </svg>
                <span class="flowcore-player__subtitle-label">Off</span>
              </button>
              <div class="flowcore-player__subtitle-menu" style="display:none;">
                <div class="flowcore-player__subtitle-option" data-track="-1">
                  <span>Off</span>
                  <svg class="flowcore-player__icon flowcore-player__icon-check" viewBox="0 0 24 24">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                  </svg>
                </div>
                ${this.options.subtitles
                  .map(
                    (sub, idx) => `
                <div class="flowcore-player__subtitle-option" data-track="${idx}">
                  <span>${sub.label}</span>
                  <svg class="flowcore-player__icon flowcore-player__icon-check" viewBox="0 0 24 24" style="display:none;">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                  </svg>
                </div>
                `,
                  )
                  .join('')}
              </div>
            </div>
            `
                : ''
            }
            
            <!-- Quality selector -->
            <div class="flowcore-player__quality-container">
              <button class="flowcore-player__btn flowcore-player__btn-quality" aria-label="Quality">
                <svg class="flowcore-player__icon" viewBox="0 0 24 24">
                  <path d="M19.43 12.98c.04-.32.07-.64.07-.98s-.03-.66-.07-.98l2.11-1.65c.19-.15.24-.42.12-.64l-2-3.46c-.12-.22-.39-.3-.61-.22l-2.49 1c-.52-.4-1.08-.73-1.69-.98l-.38-2.65C14.46 2.18 14.25 2 14 2h-4c-.25 0-.46.18-.49.42l-.38 2.65c-.61.25-1.17.59-1.69.98l-2.49-1c-.23-.09-.49 0-.61.22l-2 3.46c-.13.22-.07.49.12.64l2.11 1.65c-.04.32-.07.65-.07.98s.03.66.07.98l-2.11 1.65c-.19.15-.24.42-.12.64l2 3.46c.12.22.39.3.61.22l2.49-1c.52.4 1.08.73 1.69.98l.38 2.65c.03.24.24.42.49.42h4c.25 0 .46-.18.49-.42l.38-2.65c.61-.25 1.17-.59 1.69-.98l2.49 1c.23.09.49 0 .61-.22l2-3.46c.12-.22.07-.49-.12-.64l-2.11-1.65zM12 15.5c-1.93 0-3.5-1.57-3.5-3.5s1.57-3.5 3.5-3.5 3.5 1.57 3.5 3.5-1.57 3.5-3.5 3.5z"/>
                </svg>
                <span class="flowcore-player__quality-label">Auto</span>
              </button>
              <div class="flowcore-player__quality-menu" style="display:none;">
                <div class="flowcore-player__quality-option" data-level="-1">
                  <span>Auto</span>
                  <svg class="flowcore-player__icon flowcore-player__icon-check" viewBox="0 0 24 24">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                  </svg>
                </div>
              </div>
            </div>
            
            <!-- Fullscreen -->
            <button class="flowcore-player__btn flowcore-player__btn-fullscreen" aria-label="Fullscreen">
              <svg class="flowcore-player__icon flowcore-player__icon-fullscreen" viewBox="0 0 24 24">
                <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
              </svg>
              <svg class="flowcore-player__icon flowcore-player__icon-fullscreen-exit" viewBox="0 0 24 24" style="display:none;">
                <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    `;

    this.container.appendChild(this.controlsContainer);

    // Setup control event listeners
    this.setupControlsListeners();
  }

  private async init() {
    console.log('🚀 FlowcorePlayer.init() started');
    console.log('Options:', {
      videoId: this.options.videoId,
      streamUrl: this.options.streamUrl,
      apiUrl: this.options.apiUrl,
    });

    try {
      // Get stream URL
      if (this.options.streamUrl) {
        console.log('Using provided streamUrl');
        this.streamUrl = this.options.streamUrl;
      } else if (this.options.videoId) {
        console.log('Fetching stream URL for videoId:', this.options.videoId);
        this.streamUrl = await this.fetchStreamUrl(this.options.videoId);
        console.log('Got stream URL:', this.streamUrl);
      } else {
        throw new Error('Either videoId or streamUrl is required');
      }

      // Load video
      console.log('Loading video with URL:', this.streamUrl);
      await this.loadVideo(this.streamUrl);

      // Setup event listeners
      this.setupEventListeners();

      // Ready callback
      console.log('Player ready!');
      this.options.onReady?.();
    } catch (error) {
      console.error('❌ FlowcorePlayer initialization error:', error);
      this.options.onError?.(error as Error);
    }
  }

  private async fetchStreamUrl(videoId: string): Promise<string> {
    const apiUrl = this.options.apiUrl || 'http://localhost:3000';

    // If we have an access token, fetch the actual manifest URL from API
    if (this.options.accessToken) {
      const response = await fetch(`${apiUrl}/stream/${videoId}/manifest.m3u8?token=${this.options.accessToken}`);

      if (!response.ok) {
        throw new Error(`Failed to get stream URL: ${response.statusText}`);
      }

      const data = await response.json();
      // Relative proxy URLs (e.g. /hls-proxy/...) need the API base URL prepended
      if (data.manifestUrl.startsWith('/')) {
        return `${apiUrl}${data.manifestUrl}`;
      }
      return data.manifestUrl;
    }

    // Otherwise, generate a token first
    if (this.options.apiKey) {
      const tokenResponse = await fetch(`${apiUrl}/videos/${videoId}/token`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${this.options.apiKey}`,
          'Content-Type': 'application/json',
        },
      });

      if (!tokenResponse.ok) {
        throw new Error(`Failed to get access token: ${tokenResponse.statusText}`);
      }

      const tokenData = await tokenResponse.json();

      // Now fetch the actual manifest URL
      const streamResponse = await fetch(`${apiUrl}/stream/${videoId}/manifest.m3u8?token=${tokenData.token}`);

      if (!streamResponse.ok) {
        throw new Error(`Failed to get stream URL: ${streamResponse.statusText}`);
      }

      const streamData = await streamResponse.json();
      if (streamData.manifestUrl.startsWith('/')) {
        return `${apiUrl}${streamData.manifestUrl}`;
      }
      return streamData.manifestUrl;
    }

    // No auth - this won't work with current setup
    throw new Error('Authentication required. Please provide accessToken or apiKey.');
  }

  private async loadVideo(url: string) {
    console.log('🎬 loadVideo called with URL:', url);

    // Check if URL has authentication (SAS token)
    const hasAuth = url.includes('sv=') || url.includes('sig=');
    console.log('URL has authentication:', hasAuth);

    // Check if browser supports HLS natively (Safari)
    const canPlayNatively = this.video.canPlayType('application/vnd.apple.mpegurl');
    console.log('Native HLS support:', canPlayNatively);

    // Prefer HLS.js for consistent behavior (autoplay, quality switching).
    // Only fall back to native HLS if HLS.js is not supported (e.g. old Safari).
    if (Hls.isSupported()) {
      console.log('Using HLS.js for playback');
    } else if (canPlayNatively) {
      console.log('HLS.js not supported, falling back to native HLS playback');
      this.video.src = url;
      return;
    }

    if (Hls.isSupported()) {
      // Extract SAS token from the main URL (if present)
      let sasParams = '';
      try {
        const mainUrl = new URL(url, window.location.origin);
        sasParams = mainUrl.search;
      } catch {
        // Relative URL without SAS params (proxy URLs)
      }
      console.log('Extracted SAS params:', sasParams);
      console.log('Creating HLS.js instance...');

      this.hls = new Hls({
        enableWorker: true,
        lowLatencyMode: false,
        backBufferLength: 90,
        xhrSetup: (xhr, requestUrl) => {
          // Always append SAS params if not present
          if (sasParams && !requestUrl.includes('sv=')) {
            const separator = requestUrl.includes('?') ? '&' : '?';
            const sasParamsClean = sasParams.startsWith('?') ? sasParams.substring(1) : sasParams;
            const finalUrl = requestUrl + separator + sasParamsClean;

            console.log('🔧 XHR Setup:');
            console.log('  Request URL:', requestUrl);
            console.log('  SAS params length:', sasParamsClean.length);
            console.log('  Final URL length:', finalUrl.length);
            console.log('  Final URL (first 200):', finalUrl.substring(0, 200));
            console.log('  Final URL (last 100):', finalUrl.substring(finalUrl.length - 100));

            // Override xhr.open BEFORE it's called
            const originalOpen = xhr.open.bind(xhr);
            xhr.open = (
              method: string,
              _url: string | URL,
              async?: boolean,
              username?: string | null,
              password?: string | null,
            ) => {
              console.log(
                '  🚀 Actually opening:',
                `${finalUrl.substring(0, 100)}...${finalUrl.substring(finalUrl.length - 50)}`,
              );
              // Ignore the url parameter, use our finalUrl
              return originalOpen(method, finalUrl, async !== undefined ? async : true, username, password);
            };
          }

          xhr.withCredentials = false;
        },
      });

      this.hls.on(Hls.Events.MANIFEST_PARSED, () => {
        console.log('✅ HLS manifest parsed successfully');
      });

      console.log('Loading source:', url);
      this.hls.loadSource(url);

      console.log('Attaching media element');
      this.hls.attachMedia(this.video);

      this.hls.on(Hls.Events.ERROR, (_event, data) => {
        console.error('HLS error:', data);

        if (data.details === 'manifestLoadError') {
          console.error('Failed to load manifest:', data.url);
        }
        if (data.details === 'levelLoadError') {
          console.error('Failed to load level:', data.url);
        }
        if (data.fatal) {
          console.error('Fatal HLS error:', data);
          this.options.onError?.(new Error(`HLS error: ${data.type}`));

          // Try to recover
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              this.hls?.startLoad();
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              this.hls?.recoverMediaError();
              break;
            default:
              this.destroy();
              break;
          }
        }
      });
    } else {
      throw new Error('HLS is not supported in this browser');
    }
  }

  private setupEventListeners() {
    this.video.addEventListener('play', () => {
      this.updatePlayPauseButton();
      this.options.onPlay?.();
    });

    this.video.addEventListener('pause', () => {
      this.updatePlayPauseButton();
      this.options.onPause?.();
    });

    this.video.addEventListener('ended', () => this.options.onEnded?.());
    this.video.addEventListener('error', (_e) => {
      this.options.onError?.(new Error('Video playback error'));
    });

    this.video.addEventListener('timeupdate', () => {
      this.options.onTimeUpdate?.(this.video.currentTime);

      if (this.video.duration) {
        const percent = (this.video.currentTime / this.video.duration) * 100;
        this.options.onProgress?.(percent);
        this.updateProgressBar();
        this.updateTimeDisplay();
      }
    });

    this.video.addEventListener('progress', () => {
      this.updateBufferedBar();
    });

    this.video.addEventListener('loadedmetadata', () => {
      this.updateTimeDisplay();
      this.updateQualityMenu();

      // Restore subtitle preference from localStorage
      if (this.options.subtitles && this.options.subtitles.length > 0) {
        const videoId = this.options.videoId;
        if (videoId) {
          const savedTrackIndex = localStorage.getItem(`subtitle_${videoId}`);
          if (savedTrackIndex !== null) {
            const trackIndex = parseInt(savedTrackIndex, 10);
            if (trackIndex >= -1 && trackIndex < this.video.textTracks.length) {
              this.setSubtitle(trackIndex);
            }
          } else if (this.options.subtitles.some((s) => s.default)) {
            // If no saved preference, use default if specified
            const defaultIndex = this.options.subtitles.findIndex((s) => s.default);
            if (defaultIndex >= 0) {
              this.setSubtitle(defaultIndex);
            }
          }
        }
      }
    });
  }

  private setupControlsListeners() {
    if (!this.controlsContainer) return;

    // Show/hide controls on mouse movement / touch
    this.container.addEventListener('mousemove', () => this.showControls());
    this.container.addEventListener('touchstart', () => this.showControls());
    this.container.addEventListener('mouseleave', () => this.hideControls());

    // Click on video to play/pause
    this.video.addEventListener('click', () => this.togglePlayPause());

    // Play/pause button
    const playBtn = this.controlsContainer.querySelector('.flowcore-player__btn-play');
    playBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.togglePlayPause();
    });

    // Progress bar seeking
    const progressContainer = this.controlsContainer.querySelector('.flowcore-player__progress-container');
    progressContainer?.addEventListener('mousedown', (e) => this.startSeek(e as MouseEvent));
    progressContainer?.addEventListener('touchstart', (e) => this.startSeek(e as TouchEvent));

    // Volume button
    const volumeBtn = this.controlsContainer.querySelector('.flowcore-player__btn-volume');
    volumeBtn?.addEventListener('click', () => this.toggleMute());

    // Volume slider
    const volumeSlider = this.controlsContainer.querySelector('.flowcore-player__volume-slider') as HTMLInputElement;
    volumeSlider?.addEventListener('input', (e) => {
      const value = parseFloat((e.target as HTMLInputElement).value) / 100;
      this.setVolume(value);
      if (value > 0 && this.video.muted) {
        this.setMuted(false);
      }
    });

    // Quality button
    const qualityBtn = this.controlsContainer.querySelector('.flowcore-player__btn-quality');
    qualityBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.toggleQualityMenu();
    });

    // Subtitle button
    if (this.options.subtitles && this.options.subtitles.length > 0) {
      const subtitleBtn = this.controlsContainer.querySelector('.flowcore-player__btn-subtitle');
      subtitleBtn?.addEventListener('click', (e) => {
        e.stopPropagation();
        this.toggleSubtitleMenu();
      });

      // Subtitle menu options
      const subtitleMenu = this.controlsContainer.querySelector('.flowcore-player__subtitle-menu');
      const subtitleOptions = subtitleMenu?.querySelectorAll('.flowcore-player__subtitle-option');
      subtitleOptions?.forEach((option, _index) => {
        option.addEventListener('click', () => {
          const trackIndex = parseInt((option as HTMLElement).dataset.track || '-1', 10);
          this.setSubtitle(trackIndex);
          this.toggleSubtitleMenu();
        });
      });

      // Initialize subtitle state
      this.updateSubtitleSelection();
    }

    // Fullscreen button
    const fullscreenBtn = this.controlsContainer.querySelector('.flowcore-player__btn-fullscreen');
    fullscreenBtn?.addEventListener('click', () => this.toggleFullscreen());

    // Keyboard shortcuts
    this.container.addEventListener('keydown', (e) => this.handleKeyboard(e));
    this.container.tabIndex = 0; // Make container focusable
  }

  private showControls() {
    if (!this.controlsContainer) return;

    this.controlsContainer.classList.add('flowcore-player__controls--visible');
    this.controlsVisible = true;

    // Auto-hide after 3 seconds if playing
    if (this.controlsTimeout) {
      clearTimeout(this.controlsTimeout);
    }

    if (!this.video.paused) {
      this.controlsTimeout = window.setTimeout(() => this.hideControls(), 3000);
    }
  }

  private hideControls() {
    if (!this.controlsContainer || this.video.paused || this.isSeeking) return;

    this.controlsContainer.classList.remove('flowcore-player__controls--visible');
    this.controlsVisible = false;
  }

  private togglePlayPause() {
    if (this.video.paused) {
      this.play();
    } else {
      this.pause();
    }
  }

  private updatePlayPauseButton() {
    if (!this.controlsContainer) return;

    const playIcon = this.controlsContainer.querySelector('.flowcore-player__icon-play');
    const pauseIcon = this.controlsContainer.querySelector('.flowcore-player__icon-pause');

    if (this.video.paused) {
      (playIcon as HTMLElement).style.display = 'block';
      (pauseIcon as HTMLElement).style.display = 'none';
    } else {
      (playIcon as HTMLElement).style.display = 'none';
      (pauseIcon as HTMLElement).style.display = 'block';
    }
  }

  private updateProgressBar() {
    if (!this.controlsContainer || !this.video.duration) return;

    const percent = (this.video.currentTime / this.video.duration) * 100;
    const progressPlayed = this.controlsContainer.querySelector('.flowcore-player__progress-played') as HTMLElement;

    if (progressPlayed) {
      progressPlayed.style.width = `${percent}%`;
    }
  }

  private updateBufferedBar() {
    if (!this.controlsContainer || !this.video.duration) return;

    const buffered = this.video.buffered;
    if (buffered.length === 0) return;

    const bufferedEnd = buffered.end(buffered.length - 1);
    const percent = (bufferedEnd / this.video.duration) * 100;
    const progressBuffered = this.controlsContainer.querySelector('.flowcore-player__progress-buffered') as HTMLElement;

    if (progressBuffered) {
      progressBuffered.style.width = `${percent}%`;
    }
  }

  private updateTimeDisplay() {
    if (!this.controlsContainer) return;

    const currentEl = this.controlsContainer.querySelector('.flowcore-player__time-current');
    const durationEl = this.controlsContainer.querySelector('.flowcore-player__time-duration');

    if (currentEl) currentEl.textContent = this.formatTime(this.video.currentTime);
    if (durationEl) durationEl.textContent = this.formatTime(this.video.duration || 0);
  }

  private formatTime(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  private startSeek(e: MouseEvent | TouchEvent) {
    e.preventDefault();
    this.isSeeking = true;

    const seek = (moveEvent: MouseEvent | TouchEvent) => {
      const progressContainer = this.controlsContainer?.querySelector('.flowcore-player__progress-container');
      if (!progressContainer) return;

      const rect = progressContainer.getBoundingClientRect();
      const clientX = 'touches' in moveEvent ? moveEvent.touches[0].clientX : moveEvent.clientX;
      const x = clientX - rect.left;
      const percent = Math.max(0, Math.min(1, x / rect.width));
      const time = percent * this.video.duration;

      this.video.currentTime = time;
      this.updateProgressBar();
    };

    const endSeek = () => {
      this.isSeeking = false;
      document.removeEventListener('mousemove', seek);
      document.removeEventListener('touchmove', seek);
      document.removeEventListener('mouseup', endSeek);
      document.removeEventListener('touchend', endSeek);
    };

    seek(e);
    document.addEventListener('mousemove', seek);
    document.addEventListener('touchmove', seek);
    document.addEventListener('mouseup', endSeek);
    document.addEventListener('touchend', endSeek);
  }

  private toggleMute() {
    this.setMuted(!this.video.muted);
  }

  private updateVolumeButton() {
    if (!this.controlsContainer) return;

    const highIcon = this.controlsContainer.querySelector('.flowcore-player__icon-volume-high');
    const mutedIcon = this.controlsContainer.querySelector('.flowcore-player__icon-volume-muted');

    if (this.video.muted || this.video.volume === 0) {
      (highIcon as HTMLElement).style.display = 'none';
      (mutedIcon as HTMLElement).style.display = 'block';
    } else {
      (highIcon as HTMLElement).style.display = 'block';
      (mutedIcon as HTMLElement).style.display = 'none';
    }
  }

  private toggleQualityMenu() {
    if (!this.controlsContainer) return;

    const menu = this.controlsContainer.querySelector('.flowcore-player__quality-menu') as HTMLElement;
    if (!menu) return;

    const isVisible = menu.style.display !== 'none';
    menu.style.display = isVisible ? 'none' : 'block';
  }

  private updateQualityMenu() {
    if (!this.controlsContainer || !this.hls) return;

    const menu = this.controlsContainer.querySelector('.flowcore-player__quality-menu');
    if (!menu) return;

    const levels = this.hls.levels;
    if (levels.length === 0) return;

    // Clear existing options except Auto
    const autoOption = menu.querySelector('[data-level="-1"]');
    menu.innerHTML = '';
    if (autoOption) menu.appendChild(autoOption);

    // Add quality options
    levels.forEach((level, index) => {
      const option = document.createElement('div');
      option.className = 'flowcore-player__quality-option';
      option.setAttribute('data-level', index.toString());
      option.innerHTML = `
        <span>${level.height}p</span>
        <svg class="flowcore-player__icon flowcore-player__icon-check" viewBox="0 0 24 24" style="display:none;">
          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
        </svg>
      `;

      option.addEventListener('click', () => {
        this.setQuality(index);
        this.toggleQualityMenu();
      });

      menu.appendChild(option);
    });

    // Auto option click handler
    autoOption?.addEventListener('click', () => {
      this.setQuality('auto');
      this.toggleQualityMenu();
    });

    this.updateQualitySelection();
  }

  private updateQualitySelection() {
    if (!this.controlsContainer || !this.hls) return;

    const currentLevel = this.hls.currentLevel;
    const options = this.controlsContainer.querySelectorAll('.flowcore-player__quality-option');

    options.forEach((option) => {
      const level = parseInt(option.getAttribute('data-level') || '-1', 10);
      const checkIcon = option.querySelector('.flowcore-player__icon-check') as HTMLElement;

      if (level === currentLevel) {
        checkIcon.style.display = 'block';
      } else {
        checkIcon.style.display = 'none';
      }
    });

    // Update label
    const label = this.controlsContainer.querySelector('.flowcore-player__quality-label');
    if (label) {
      if (currentLevel === -1) {
        label.textContent = 'Auto';
      } else {
        const level = this.hls.levels[currentLevel];
        label.textContent = level ? `${level.height}p` : 'Auto';
      }
    }
  }

  private toggleFullscreen() {
    if (!document.fullscreenElement) {
      this.container.requestFullscreen().catch((err) => {
        console.error('Fullscreen error:', err);
      });
    } else {
      document.exitFullscreen();
    }

    this.updateFullscreenButton();
  }

  private updateFullscreenButton() {
    if (!this.controlsContainer) return;

    const enterIcon = this.controlsContainer.querySelector('.flowcore-player__icon-fullscreen');
    const exitIcon = this.controlsContainer.querySelector('.flowcore-player__icon-fullscreen-exit');

    if (document.fullscreenElement) {
      (enterIcon as HTMLElement).style.display = 'none';
      (exitIcon as HTMLElement).style.display = 'block';
    } else {
      (enterIcon as HTMLElement).style.display = 'block';
      (exitIcon as HTMLElement).style.display = 'none';
    }
  }

  private handleKeyboard(e: KeyboardEvent) {
    switch (e.key) {
      case ' ':
      case 'k':
        e.preventDefault();
        this.togglePlayPause();
        break;
      case 'ArrowLeft':
        e.preventDefault();
        this.seek(Math.max(0, this.video.currentTime - 10));
        break;
      case 'ArrowRight':
        e.preventDefault();
        this.seek(Math.min(this.video.duration, this.video.currentTime + 10));
        break;
      case 'ArrowUp':
        e.preventDefault();
        this.setVolume(Math.min(1, this.video.volume + 0.1));
        break;
      case 'ArrowDown':
        e.preventDefault();
        this.setVolume(Math.max(0, this.video.volume - 0.1));
        break;
      case 'm':
        e.preventDefault();
        this.toggleMute();
        break;
      case 'f':
        e.preventDefault();
        this.toggleFullscreen();
        break;
    }
  }

  // Public API
  play(): Promise<void> {
    return this.video.play();
  }

  pause(): void {
    this.video.pause();
  }

  seek(seconds: number): void {
    this.video.currentTime = seconds;
  }

  setVolume(volume: number): void {
    this.video.volume = Math.max(0, Math.min(1, volume));

    // Update volume slider
    if (this.controlsContainer) {
      const slider = this.controlsContainer.querySelector('.flowcore-player__volume-slider') as HTMLInputElement;
      if (slider) slider.value = (volume * 100).toString();
    }

    this.updateVolumeButton();
  }

  setMuted(muted: boolean): void {
    this.video.muted = muted;
    this.updateVolumeButton();
  }

  getVolume(): number {
    return this.video.volume;
  }

  getCurrentTime(): number {
    return this.video.currentTime;
  }

  getDuration(): number {
    return this.video.duration;
  }

  isPaused(): boolean {
    return this.video.paused;
  }

  isMuted(): boolean {
    return this.video.muted;
  }

  // Quality control (if using hls.js)
  setQuality(level: number | 'auto'): void {
    if (!this.hls) return;

    if (level === 'auto') {
      this.hls.currentLevel = -1; // Auto quality
    } else {
      this.hls.currentLevel = level;
    }

    this.updateQualitySelection();
  }

  getQualityLevels(): Array<{ index: number; height: number; bitrate: number }> {
    if (!this.hls) return [];

    return this.hls.levels.map((level, index) => ({
      index,
      height: level.height,
      bitrate: level.bitrate,
    }));
  }

  getCurrentQuality(): number {
    return this.hls?.currentLevel ?? -1;
  }

  private toggleSubtitleMenu() {
    if (!this.controlsContainer) return;

    const menu = this.controlsContainer.querySelector('.flowcore-player__subtitle-menu') as HTMLElement;
    if (!menu) return;

    const isVisible = menu.style.display !== 'none';
    menu.style.display = isVisible ? 'none' : 'block';

    // Close quality menu if open
    const qualityMenu = this.controlsContainer.querySelector('.flowcore-player__quality-menu') as HTMLElement;
    if (qualityMenu) qualityMenu.style.display = 'none';
  }

  private setSubtitle(trackIndex: number) {
    const tracks = this.video.textTracks;

    console.log(`🎬 Setting subtitle track ${trackIndex}, total tracks: ${tracks.length}`);

    // Turn off all tracks
    for (let i = 0; i < tracks.length; i++) {
      tracks[i].mode = 'hidden';
    }

    // Enable selected track (or none if -1)
    if (trackIndex >= 0 && trackIndex < tracks.length) {
      const track = tracks[trackIndex];
      console.log(`📝 Enabling track ${trackIndex}:`, {
        label: track.label,
        language: track.language,
        readyState: track.readyState, // 0 = NONE, 1 = LOADING, 2 = LOADED, 3 = ERROR
        mode: track.mode,
      });

      // Check if track is loaded
      if (track.readyState === 2) {
        // LOADED
        track.mode = 'showing';
        console.log(`✅ Track ${trackIndex} set to showing`);
      } else if (track.readyState === 3) {
        // ERROR
        console.error(`❌ Track ${trackIndex} failed to load`);
      } else {
        // Track is still loading, wait for it
        track.mode = 'showing';
        track.addEventListener(
          'load',
          () => {
            console.log(`✅ Track ${trackIndex} loaded and showing`);
          },
          { once: true },
        );
      }
    }

    // Store preference in localStorage
    const videoId = this.options.videoId;
    if (videoId) {
      localStorage.setItem(`subtitle_${videoId}`, trackIndex.toString());
    }

    this.updateSubtitleSelection();
  }

  private updateSubtitleSelection() {
    if (!this.controlsContainer || !this.options.subtitles) return;

    const tracks = this.video.textTracks;
    let activeTrackIndex = -1;

    // Find active track
    for (let i = 0; i < tracks.length; i++) {
      if (tracks[i].mode === 'showing') {
        activeTrackIndex = i;
        break;
      }
    }

    // Update menu checkmarks
    const options = this.controlsContainer.querySelectorAll('.flowcore-player__subtitle-option');
    options.forEach((option) => {
      const trackIndex = parseInt((option as HTMLElement).dataset.track || '-1', 10);
      const checkIcon = option.querySelector('.flowcore-player__icon-check') as HTMLElement;

      if (trackIndex === activeTrackIndex) {
        checkIcon.style.display = 'block';
      } else {
        checkIcon.style.display = 'none';
      }
    });

    // Update label
    const label = this.controlsContainer.querySelector('.flowcore-player__subtitle-label');
    if (label) {
      if (activeTrackIndex >= 0 && this.options.subtitles[activeTrackIndex]) {
        label.textContent = this.options.subtitles[activeTrackIndex].label;
      } else {
        label.textContent = 'Off';
      }
    }
  }

  destroy(): void {
    if (this.hls) {
      this.hls.destroy();
      this.hls = null;
    }

    this.video.pause();
    this.video.src = '';
    this.video.load();
    this.container.innerHTML = '';
  }
}

// Export for direct script tag usage
if (typeof window !== 'undefined') {
  (window as any).FlowcorePlayer = FlowcorePlayer;
}
